import dayjs from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
	SUPPORTED_LANGUAGES,
	DEFAULT_LANGUAGE,
	type SupportedLanguage
} from '@/constants/languages';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

dayjs.extend(utc);
dayjs.extend(timezone);

export const getLanguageFromPath = (pathname: string): SupportedLanguage => {
	const langFromPath = pathname.split('/')[1];
	const isSupportedLanguage = SUPPORTED_LANGUAGES.includes(langFromPath as SupportedLanguage);
	return isSupportedLanguage ? (langFromPath as SupportedLanguage) : DEFAULT_LANGUAGE;
};

export const formatDateTime = (
	dateString: string,
	locale = 'en',
	timeZone = 'America/Mexico_City'
) => {
	if (!dateString) return '';

	const date = dayjs(dateString);
	if (!date.isValid()) return 'Invalid Date';

	// Set the time zone
	const zonedDate = date.tz(timeZone);

	// Define the format based on the locale
	const format = locale === 'es' ? 'DD MMM YYYY, h:mm A' : 'MMM DD, YYYY, h:mm A';

	// Format the date with the given locale
	return zonedDate.locale(locale).format(format);
};

export const formatMoney = (money?: string | null) => {
	if (!money) return '';
	// Remove currency symbols, spaces, and commas to get pure numeric value
	const cleanedMoney = money.replace(/[$,\s]/g, '');
	const numericMatch = cleanedMoney.match(/(\d+(?:\.\d+)?)/);

	if (!numericMatch) {
		return '';
	}

	const numericValue = parseFloat(numericMatch[0]);

	return numericValue.toLocaleString('es-MX', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	});
};

export const validatePassword = (password: string): boolean => {
	if (!password || password.length < 8) {
		return false;
	}

	const hasUpperCase = /[A-Z]/.test(password);
	const hasLowerCase = /[a-z]/.test(password);
	const hasNumber = /[0-9]/.test(password);
	const hasSpecialChar = /[#?!@$%^&*-]/.test(password);

	return hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
};

export const pascalToConstantCase = (input: string): string => {
	if (!input) return '';

	// Check if string is already in constant case (all caps with underscores)
	if (/^[A-Z][A-Z0-9_]*$/.test(input)) {
		return input;
	}

	return input
		.replace(/([A-Z])/g, (match, _group, offset) => (offset > 0 ? '_' : '') + match)
		.toUpperCase();
};

export const constantToPascalCase = (constantCase: string): string => {
	if (!constantCase) {
		return '';
	}

	return constantCase
		.toLowerCase()
		.split('_')
		.map((word) => {
			return word.charAt(0).toUpperCase() + word.slice(1);
		})
		.join('');
};

export const formatPhoneNumber = (phoneNumber: string): string => {
	const cleanedNumber = phoneNumber.replace(/\D/g, '');

	const countryCode = cleanedNumber.slice(0, cleanedNumber.length - 10);
	const localNumber = cleanedNumber.slice(-10);

	const formattedLocalNumber = `(${localNumber.slice(0, 3)}) ${localNumber.slice(3, 6)}-${localNumber.slice(6)}`;

	return `+${countryCode} ${formattedLocalNumber}`;
};

export function formatBytes(bytes: number): string {
	if (bytes < 1024) {
		return `${bytes} bytes`;
	} else if (bytes < 1048576) {
		return `${(bytes / 1024).toFixed(2)} KB`;
	} else {
		return `${(bytes / 1048576).toFixed(2)} MB`;
	}
}

export const formatMoneyValue = (value: string | undefined | null): string => {
	if (!value) return '';
	const digits = value.replace(/\D/g, '');

	const number = parseInt(digits, 10) / 100;

	if (isNaN(number)) return '';

	return new Intl.NumberFormat('es-MX', {
		style: 'currency',
		currency: 'MXN',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(number);
};

export const formatPercentageValue = (value: string): string => {
	const digits = value.replace(/\D/g, '');

	const number = parseInt(digits, 10) / 100;

	if (isNaN(number)) return '';

	return `${number.toFixed(2)}%`;
};

export const parseMoneyValue = (formattedValue: string): string => {
	return formattedValue.replace(/[^0-9.]/g, '');
};

export const parsePercentageValue = (formattedValue: string): string => {
	return formattedValue.replace(/[^0-9.]/g, '');
};

export function parseAccountingToMoney(
	accountingString: string,
	currency: string = 'MXN'
): string | null {
	if (!accountingString || typeof accountingString !== 'string') {
		return '0.00 ' + currency;
	}
	const cleanedString = accountingString.replace(/[$\s]/g, '');

	const numericString = cleanedString.replace(/,/g, '');

	const number = parseFloat(numericString);
	const formattedNumber = number.toFixed(2);

	return `${formattedNumber} ${currency}`;
}

export const formatDate = (dateString: string, locale = 'es'): string => {
	if (!dateString) return '';

	const date = dayjs(dateString);
	if (!date.isValid()) return 'Invalid Date';

	const format = locale === 'es' ? 'DD MMM, YYYY' : 'MMM DD, YYYY';
	return date.locale(locale).format(format);
};

export const formatCurrency = (amount: number) => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD'
	}).format(amount);
};

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}
